/**
 * Frontend Logger Utility
 * 
 * Provides comprehensive logging for API calls, errors, and application events.
 * Logs are stored in memory and can be downloaded as a file.
 * Errors and warnings are also forwarded to Azure Application Insights.
 * 
 * Features:
 * - Structured logging with timestamps
 * - Log levels (INFO, WARN, ERROR, DEBUG)
 * - Automatic log file generation
 * - Connection status tracking
 * - 500 error detailed logging
 * - Application Insights integration for errors/warnings
 */

import { getAppInsights } from '../config/telemetry';

class Logger {
  constructor() {
    this.logs = [];
    this.maxLogs = 1000; // Prevent memory overflow
    this.sessionId = this.generateSessionId();
    this.startTime = new Date().toISOString();
    this.autoSaveInterval = 5 * 60 * 1000; // Auto-save every 5 minutes
    this.saveOnErrorCount = 5; // Auto-save after 5 errors
    this.errorCount = 0;
    this.logFilePath = this.generateLogFilePath();
    
    // Log initial session start
    this.info('Logger initialized', { sessionId: this.sessionId });
    
    // Set up auto-save interval
    this.startAutoSave();
    
    // Save logs before page unload
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.saveLogsToFile('page-unload');
      });
    }
  }

  /**
   * Generate log file path with timestamp
   */
  generateLogFilePath() {
    const timestamp = new Date().toISOString().replace(/:/g, '-').split('.')[0];
    return `dui-frontend-logs-${timestamp}.txt`;
  }

  /**
   * Start automatic log saving
   */
  startAutoSave() {
    if (typeof window !== 'undefined') {
      this.autoSaveTimer = setInterval(() => {
        if (this.logs.length > 0) {
          this.saveLogsToFile('auto-save');
        }
      }, this.autoSaveInterval);
    }
  }

  /**
   * Stop automatic log saving
   */
  stopAutoSave() {
    if (this.autoSaveTimer) {
      clearInterval(this.autoSaveTimer);
    }
  }

  /**
   * Generate unique session ID
   */
  generateSessionId() {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Format log entry with timestamp and metadata
   */
  formatLogEntry(level, message, data = {}) {
    const timestamp = new Date().toISOString();
    const entry = {
      timestamp,
      level,
      message,
      sessionId: this.sessionId,
      url: window.location.href,
      userAgent: navigator.userAgent,
      ...data
    };

    // Add to logs array
    this.logs.push(entry);

    // Prevent memory overflow
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    return entry;
  }

  /**
   * Log INFO level message
   */
  info(message, data = {}) {
    const entry = this.formatLogEntry('INFO', message, data);
    console.log(`[INFO] ${message}`, data);
    return entry;
  }

  /**
   * Log WARNING level message
   */
  warn(message, data = {}) {
    const entry = this.formatLogEntry('WARN', message, data);
    console.warn(`[WARN] ${message}`, data);

    const ai = getAppInsights();
    if (ai) {
      ai.trackTrace({ message: `[WARN] ${message}`, severityLevel: 2, properties: data });
    }

    return entry;
  }

  /**
   * Log ERROR level message
   */
  error(message, data = {}) {
    const entry = this.formatLogEntry('ERROR', message, data);
    console.error(`[ERROR] ${message}`, data);

    const ai = getAppInsights();
    if (ai) {
      const error = data.errorStack ? new Error(message) : null;
      if (error && data.errorStack) error.stack = data.errorStack;

      if (error) {
        ai.trackException({ exception: error, properties: data });
      } else {
        ai.trackTrace({ message: `[ERROR] ${message}`, severityLevel: 3, properties: data });
      }
    }
    
    // Increment error count and auto-save if threshold reached
    this.errorCount++;
    if (this.errorCount >= this.saveOnErrorCount) {
      this.saveLogsToFile('error-threshold');
      this.errorCount = 0; // Reset counter
    }
    
    return entry;
  }

  /**
   * Log DEBUG level message
   */
  debug(message, data = {}) {
    const entry = this.formatLogEntry('DEBUG', message, data);
    console.debug(`[DEBUG] ${message}`, data);
    return entry;
  }

  /**
   * Log API request
   */
  logApiRequest(method, url, body = null) {
    return this.info('API Request', {
      method,
      url,
      body: body ? JSON.stringify(body) : null,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Log API response
   */
  logApiResponse(method, url, status, data = null, duration = null) {
    const level = status >= 500 ? 'ERROR' : status >= 400 ? 'WARN' : 'INFO';
    const message = `API Response: ${method} ${url} - Status ${status}`;
    
    return this.formatLogEntry(level, message, {
      method,
      url,
      status,
      statusText: this.getStatusText(status),
      responseData: data,
      duration,
      timestamp: new Date().toISOString()
    });
  }

  /**
   * Log 500 Internal Server Error with detailed information
   */
  log500Error(method, url, error, requestBody = null) {
    const entry = this.error('500 Internal Server Error', {
      method,
      url,
      errorMessage: error.message,
      errorStack: error.stack,
      requestBody: requestBody ? JSON.stringify(requestBody) : null,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      sessionId: this.sessionId
    });
    
    // Immediately save logs on 500 error
    this.saveLogsToFile('500-error');
    
    return entry;
  }

  /**
   * Log connection test result
   */
  logConnectionTest(url, success, error = null) {
    if (success) {
      return this.info('Backend Connection Successful', {
        url,
        timestamp: new Date().toISOString()
      });
    } else {
      return this.error('Backend Connection Failed', {
        url,
        error: error?.message || 'Unknown error',
        timestamp: new Date().toISOString()
      });
    }
  }

  /**
   * Get HTTP status text
   */
  getStatusText(status) {
    const statusTexts = {
      200: 'OK',
      201: 'Created',
      204: 'No Content',
      400: 'Bad Request',
      401: 'Unauthorized',
      403: 'Forbidden',
      404: 'Not Found',
      500: 'Internal Server Error',
      502: 'Bad Gateway',
      503: 'Service Unavailable'
    };
    return statusTexts[status] || 'Unknown';
  }

  /**
   * Get all logs
   */
  getLogs() {
    return this.logs;
  }

  /**
   * Get logs by level
   */
  getLogsByLevel(level) {
    return this.logs.filter(log => log.level === level);
  }

  /**
   * Get error logs only
   */
  getErrors() {
    return this.getLogsByLevel('ERROR');
  }

  /**
   * Clear all logs
   */
  clearLogs() {
    this.logs = [];
    this.info('Logs cleared');
  }

  /**
   * Generate log file content
   */
  generateLogFile() {
    const header = `
=============================================================
DUI First Time Offender Program - Frontend Logs
=============================================================
Session ID: ${this.sessionId}
Start Time: ${this.startTime}
Generated: ${new Date().toISOString()}
Total Logs: ${this.logs.length}
Errors: ${this.getErrors().length}
=============================================================

`;

    const logEntries = this.logs.map(log => {
      return `
[${log.timestamp}] [${log.level}] ${log.message}
${JSON.stringify(log, null, 2)}
-----------------------------------------------------------`;
    }).join('\n');

    return header + logEntries;
  }

  /**
   * Save logs to a file
   * @param {string} trigger - What triggered the save (auto-save, error-threshold, 500-error, etc.)
   */
  saveLogsToFile(trigger = 'manual') {
    if (this.logs.length === 0) return;

    const timestamp = new Date().toISOString().replace(/:/g, '-').split('.')[0];
    const filename = `dui-frontend-logs-${timestamp}-${trigger}.txt`;

    const content = this.generateLogFile();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    console.log(`[Logger] Logs saved to file: ${filename} (Trigger: ${trigger})`);
  }

  /**
   * Download logs as a file (backward compatibility)
   */
  downloadLogs(filename = null) {
    this.saveLogsToFile('manual-download');
  }

  /**
   * Get summary of logs
   */
  getSummary() {
    const errors = this.getErrors();
    const warnings = this.getLogsByLevel('WARN');
    const info = this.getLogsByLevel('INFO');
    
    return {
      sessionId: this.sessionId,
      startTime: this.startTime,
      totalLogs: this.logs.length,
      errors: errors.length,
      warnings: warnings.length,
      info: info.length,
      lastError: errors.length > 0 ? errors[errors.length - 1] : null
    };
  }

  /**
   * Export logs as JSON
   */
  exportAsJSON() {
    return {
      sessionId: this.sessionId,
      startTime: this.startTime,
      exportTime: new Date().toISOString(),
      summary: this.getSummary(),
      logs: this.logs
    };
  }
}

// No-op logger for production - all methods silently do nothing
const noop = () => {};
const noopLogger = new Proxy({}, {
  get: (_target, prop) => {
    if (prop === 'getLogs' || prop === 'getErrors' || prop === 'getLogsByLevel') return () => [];
    if (prop === 'getSummary') return () => ({ sessionId: '', startTime: '', totalLogs: 0, errors: 0, warnings: 0, info: 0, lastError: null });
    if (prop === 'exportAsJSON') return () => ({});
    if (prop === 'logs') return [];
    return noop;
  }
});

// Only enable real logging in development
const isDev = process.env.NODE_ENV === 'development';
const logger = isDev ? new Logger() : noopLogger;

// Expose logger globally for debugging (dev only)
if (isDev && typeof window !== 'undefined') {
  window.appLogger = logger;
}

export default logger;
